import { useEffect, useState, useCallback, ChangeEvent, FormEvent } from 'react';
import { useGameSocket } from '../hooks/useGameSocket';
import LiveSchedule from './LiveSchedule';

interface Ranking {
  Member: string;
  Score: number;
}

const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || '';

export default function AdminDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [authError, setAuthError] = useState('');

  const getWsUrl = () => {
    if (process.env.REACT_APP_WS_URL) return process.env.REACT_APP_WS_URL;
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    return `${protocol}//${host}/ws`;
  };
  const wsUrl = getWsUrl();
  const adminUrl = `${wsUrl}?username=admin&admin=true`;
  const handleSocketMessage = useCallback((msg: any) => {
    if (msg.type === 'GAME_STATE') {
      const { isRunning, questionsLoaded, currentQuestion, nextQuiz, playerCount: initialPlayerCount } = msg.data;
      if (initialPlayerCount !== undefined) setPlayerCount(initialPlayerCount);
      if (isRunning) {
        setGameStatus('Question in progress');
        if (currentQuestion) {
          setCurrentQuestion(currentQuestion);
          try {
            const endTime = new Date(currentQuestion.expiresAt).getTime();
            const now = Date.now();
            const remaining = Math.max(0, Math.floor((endTime - now) / 1000));
            setTimeLeft(remaining);
            setTotalTime(remaining || 60);
          } catch { }
        }
      } else if (questionsLoaded) {
        setGameStatus('Quiz loaded. Ready to start.');
      }
      setQuestionsLoaded(questionsLoaded);
      if (questionsLoaded) {
        setQuestionsInfo('Questions are already loaded on the server.');
      }
      if (nextQuiz) {
        setNextQuizTitle(nextQuiz.title || '');
        setNextQuizTime(nextQuiz.time || '');
      }
    } else if (msg.type === 'PLAYER_COUNT_UPDATE') {
      setPlayerCount(msg.data);
    }
  }, []);

  const { isConnected, latestMessage, sendMessage } = useGameSocket(isAuthenticated ? adminUrl : '', handleSocketMessage);

  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1024);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth > 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [rankings, setRankings] = useState<Ranking[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState<any>(null);
  const [gameStatus, setGameStatus] = useState<string>('Waiting for game to start...');
  const [questionsLoaded, setQuestionsLoaded] = useState<boolean>(false);
  const [questionsInfo, setQuestionsInfo] = useState<string>('No quiz loaded');
  const [language, setLanguage] = useState<'hindi' | 'english'>('hindi');
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [totalTime, setTotalTime] = useState<number>(0);
  const [showSettings, setShowSettings] = useState(false);
  const [showSponsorModal, setShowSponsorModal] = useState(false);
  const [sponsorName, setSponsorName] = useState('');
  const [sponsorImageUrl, setSponsorImageUrl] = useState('');
  const [sponsorLink, setSponsorLink] = useState('');
  const [showNextQuizModal, setShowNextQuizModal] = useState(false);
  const [nextQuizTitle, setNextQuizTitle] = useState('');
  const [nextQuizTime, setNextQuizTime] = useState('');
  const [playerCount, setPlayerCount] = useState<number>(0);
  const [showAutoPlayModal, setShowAutoPlayModal] = useState(false);
  const [schedules, setSchedules] = useState<any[]>([]);
  const [autoPlayTime, setAutoPlayTime] = useState('15:00');
  const [scheduledQuestions, setScheduledQuestions] = useState<any[] | null>(null);
  const [scheduledFileName, setScheduledFileName] = useState('');
  const [autoReveal, setAutoReveal] = useState<boolean>(() => {
    return localStorage.getItem('autoRevealAnswer') === 'true';
  });

  useEffect(() => {
    localStorage.setItem('autoRevealAnswer', String(autoReveal));
  }, [autoReveal]);

  const handleAuthSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!ADMIN_PASSWORD) {
      setAuthError('Admin password is not configured on this server.');
      return;
    }
    if (passwordInput === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setAuthError('');
    } else {
      setAuthError('Incorrect password. Please try again.');
    }
  };

  useEffect(() => {
    if (!latestMessage) return;

    if (latestMessage.type === 'TOP_RANKINGS') {
      setRankings(latestMessage.data || []);
    }

    // Critical state is now handled in callback, but keep for fallback or minor updates
    if (latestMessage.type === 'GAME_STATE') {
      const { isRunning, questionsLoaded, currentQuestion, nextQuiz, playerCount: initialPlayerCount } = latestMessage.data;
      if (initialPlayerCount !== undefined) setPlayerCount(initialPlayerCount);
      if (isRunning) {
        setGameStatus('Question in progress');
        if (currentQuestion) {
          setCurrentQuestion(currentQuestion);
          try {
            const endTime = new Date(currentQuestion.expiresAt).getTime();
            const now = Date.now();
            const remaining = Math.max(0, Math.floor((endTime - now) / 1000));
            setTimeLeft(remaining);
            setTotalTime(remaining || 60);
          } catch { }
        }
      } else if (questionsLoaded) {
        setGameStatus('Quiz loaded. Ready to start.');
      }
      setQuestionsLoaded(questionsLoaded);
      if (questionsLoaded) {
        setQuestionsInfo('Questions are already loaded on the server.');
      }
      if (nextQuiz) {
        setNextQuizTitle(nextQuiz.title || '');
        setNextQuizTime(nextQuiz.time || '');
      }
    }

    if (latestMessage.type === 'PLAYER_COUNT_UPDATE') {
      setPlayerCount(latestMessage.data);
    }

    if (latestMessage.type === 'NEW_QUESTION') {
      setCurrentQuestion(latestMessage.data);
      setGameStatus('Question in progress');
      // Setup countdown based on expiresAt
      try {
        const endTime = new Date(latestMessage.data.expiresAt).getTime();
        const now = Date.now();
        const remaining = Math.max(0, Math.floor((endTime - now) / 1000));
        setTimeLeft(remaining);
        setTotalTime(remaining || 60);
      } catch {
        setTimeLeft(0);
        setTotalTime(0);
      }
    }

    if (latestMessage.type === 'GAME_OVER') {
      const data = latestMessage.data || {};
      const winner = data.winner || data.topPlayer;
      const score =
        typeof data.score === 'number'
          ? data.score
          : typeof data.topScore === 'number'
            ? data.topScore
            : undefined;

      if (winner) {
        setGameStatus(`Game Over – Winner: ${String(winner)}${typeof score === 'number' ? ` (Score: ${Math.floor(score)})` : ''
          }`);
      } else {
        setGameStatus('Game Over');
      }

      // Keep markings and rankings visible!
      setCurrentQuestion(null);
      setTimeLeft(0);
      setTotalTime(0);
      // We don't reset questionsLoaded here so they can start again easily
    }

    if (latestMessage.type === 'SCHEDULES_UPDATE') {
      setSchedules(latestMessage.data || []);
    }
  }, [latestMessage]);

  useEffect(() => {
    if (showAutoPlayModal && isConnected) {
      sendMessage({ type: 'GET_SCHEDULES' });
    }
  }, [showAutoPlayModal, isConnected, sendMessage]);

  useEffect(() => {
    console.log("Admin Socket Status:", isConnected ? "Connected" : "Disconnected");
  }, [isConnected]);

  // Countdown timer for admin view
  useEffect(() => {
    if (!currentQuestion || !timeLeft) return;
    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        const next = prev - 1;
        return next > 0 ? next : 0;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [currentQuestion, timeLeft]);

  if (!isAuthenticated) {
    return (
      <div
        style={{
          padding: '40px',
          fontFamily: 'Segoe UI, Arial, sans-serif',
          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
          minHeight: '100vh',
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <form
          onSubmit={handleAuthSubmit}
          style={{
            maxWidth: '420px',
            width: '100%',
            background: 'rgba(0,0,0,0.35)',
            padding: '32px',
            borderRadius: '16px',
            boxShadow: '0 12px 30px rgba(0,0,0,0.6)',
            border: '1px solid rgba(255,255,255,0.15)',
          }}
        >
          <h1
            style={{
              fontSize: '2.4rem',
              marginBottom: '10px',
              textAlign: 'center',
            }}
          >
            Admin Login
          </h1>
          <p
            style={{
              fontSize: '1.2rem',
              marginBottom: '20px',
              textAlign: 'center',
              color: 'rgba(255,255,255,0.8)',
            }}
          >
            Enter the admin password to access the dashboard.
          </p>
          <label
            style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '1.1rem',
            }}
          >
            Password
          </label>
          <input
            type="password"
            value={passwordInput}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setPasswordInput(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.4)',
              fontSize: '1.1rem',
              marginBottom: '16px',
              background: 'rgba(0,0,0,0.4)',
              color: '#ffffff',
            }}
          />
          {authError && (
            <div
              style={{
                marginBottom: '12px',
                color: '#e74c3c',
                fontSize: '1rem',
              }}
            >
              {authError}
            </div>
          )}
          <button
            type="submit"
            style={{
              width: '100%',
              padding: '10px 16px',
              borderRadius: '10px',
              border: 'none',
              fontSize: '1.2rem',
              fontWeight: 600,
              cursor: 'pointer',
              background: '#27ae60',
              color: '#ffffff',
              boxShadow: '0 6px 16px rgba(0,0,0,0.5)',
              marginTop: '4px',
            }}
          >
            Enter Dashboard
          </button>
        </form>
      </div>
    );
  }


  const getQuestionText = () => {
    if (!currentQuestion) return '';
    if (language === 'english' && currentQuestion.textEnglish) return currentQuestion.textEnglish;
    return currentQuestion.text;
  };

  const getOptionText = (opt: any) => {
    if (language === 'english' && opt.textEnglish) return opt.textEnglish;
    return opt.text;
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isConnected) {
      alert('Admin socket not connected yet.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result));
        if (!Array.isArray(parsed)) {
          alert('JSON must be an array of questions.');
          return;
        }
        sendMessage({
          type: 'LOAD_QUESTIONS',
          data: parsed,
        });
        setQuestionsLoaded(true);
        setQuestionsInfo(`Loaded ${parsed.length} questions from "${file.name}"`);
        setGameStatus('Quiz loaded. Ready to start.');
        setRankings([]); // Clear rankings for the new quiz
      } catch (err) {
        console.error('Failed to parse quiz JSON:', err);
        alert('Invalid JSON file. See console for details.');
      }
    };
    reader.readAsText(file);
  };

  const handleScheduledFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result));
        if (!Array.isArray(parsed)) {
          alert('JSON must be an array of questions.');
          return;
        }
        setScheduledQuestions(parsed);
        setScheduledFileName(file.name);
      } catch (err) {
        alert('Invalid JSON file.');
      }
    };
    reader.readAsText(file);
  };

  const handleAddSchedule = () => {
    if (!scheduledQuestions) {
      alert('Please upload a quiz file first.');
      return;
    }
    sendMessage({
      type: 'ADD_SCHEDULE',
      time: autoPlayTime,
      questions: scheduledQuestions,
      active: true,
    });
    setScheduledQuestions(null);
    setScheduledFileName('');
    alert('Schedule added!');
  };

  const handleDeleteSchedule = (id: string) => {
    if (window.confirm('Delete this schedule?')) {
      sendMessage({
        type: 'DELETE_SCHEDULE',
        id: id,
      });
    }
  };

  const handleStartGame = () => {
    if (!isConnected) {
      alert('Admin socket not connected yet.');
      return;
    }
    if (!questionsLoaded) {
      alert('Please load a quiz JSON file before starting the game.');
      return;
    }
    const msg = currentQuestion
      ? "A game is already in progress! Starting a new one will WIPE all current scores. Are you sure?"
      : "Confirm: Start the game now?";
    if (!window.confirm(msg)) return;

    sendMessage({
      type: 'START_GAME',
    });
    setGameStatus('Game starting...');
    setRankings([]); // Clear rankings for the new start
  };

  const handleEndGame = () => {
    if (!isConnected) return;
    if (window.confirm("Are you sure you want to stop the game immediately?")) {
      sendMessage({ type: 'END_GAME' });
    }
  };

  const handleSponsorSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!isConnected) return;
    sendMessage({
      type: 'SET_SPONSOR',
      name: sponsorName,
      imageUrl: sponsorImageUrl,
      link: sponsorLink,
    });
    setShowSponsorModal(false);
    alert('Sponsor updated successfully!');
  };

  const handleNextQuizSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!isConnected) return;
    sendMessage({
      type: 'SET_NEXT_QUIZ',
      title: nextQuizTitle,
      time: nextQuizTime,
    });
    setShowNextQuizModal(false);
    alert('Next quiz announcement updated!');
  };

  return (
    <div
      style={{
        padding: '40px',
        fontFamily: 'Segoe UI, Arial, sans-serif',
        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
        minHeight: '100vh',
        color: '#ffffff',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '30px', gap: '20px' }}>
        <img
          src="/logo_custom.png"
          alt="Logo"
          style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            border: '2px solid rgba(255,255,255,0.2)',
            boxShadow: '0 4px 10px rgba(0,0,0,0.3)'
          }}
        />
        <h1
          style={{
            margin: 0,
            fontSize: '3rem',
            fontWeight: 'bold',
            color: '#ffffff',
            textShadow: '2px 2px 8px rgba(0,0,0,0.5)',
          }}
        >
          Crack It Quizzes – Admin Dashboard
        </h1>
      </div>

      {/* Settings Gear */}
      <div style={{ position: 'absolute', top: '20px', right: '40px', display: 'flex', gap: '15px', alignItems: 'center' }}>
        {/* Auto Reveal Status Indicator */}
        <div
          onClick={() => setAutoReveal(!autoReveal)}
          style={{
            padding: '8px 16px',
            borderRadius: '20px',
            background: autoReveal ? 'rgba(46, 204, 113, 0.2)' : 'rgba(255,255,255,0.1)',
            border: `1px solid ${autoReveal ? '#2ecc71' : 'rgba(255,255,255,0.2)'}`,
            color: autoReveal ? '#2ecc71' : '#ffffff',
            fontSize: '1rem',
            fontWeight: 800,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            backdropFilter: 'blur(5px)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.background = autoReveal ? 'rgba(46, 204, 113, 0.3)' : 'rgba(255,255,255,0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.background = autoReveal ? 'rgba(46, 204, 113, 0.2)' : 'rgba(255,255,255,0.1)';
          }}
        >
          <span style={{
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            background: autoReveal ? '#2ecc71' : '#bdc3c7',
            display: 'inline-block',
            boxShadow: autoReveal ? '0 0 10px #2ecc71' : 'none'
          }}></span>
          Auto Reveal: {autoReveal ? 'ON' : 'OFF'}
        </div>

        <button
          onClick={() => setShowSettings(!showSettings)}
          style={{
            background: 'none',
            border: 'none',
            color: '#ffffff',
            fontSize: '2rem',
            cursor: 'pointer',
            padding: '10px',
            borderRadius: '50%',
            transition: 'background 0.3s',
            zIndex: 101,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.1)')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
        >
          ⚙️
        </button>
        {showSettings && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              background: '#ffffff',
              color: '#333333',
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              padding: '10px 0',
              zIndex: 100,
              minWidth: '160px',
            }}
          >
            <button
              onClick={() => {
                setShowSponsorModal(true);
                setShowSettings(false);
              }}
              style={{
                width: '100%',
                padding: '10px 20px',
                textAlign: 'left',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 600,
              }}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
            >
              Add Sponsor
            </button>
            <button
              onClick={() => {
                setShowNextQuizModal(true);
                setShowSettings(false);
              }}
              style={{
                width: '100%',
                padding: '10px 20px',
                textAlign: 'left',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 600,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#f0f0f0')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
            >
              Announce Next Quiz
            </button>
            <button
              onClick={() => {
                setShowAutoPlayModal(true);
                setShowSettings(false);
              }}
              style={{
                width: '100%',
                padding: '10px 20px',
                textAlign: 'left',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 600,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#f0f0f0')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
            >
              Auto Play Settings
            </button>
          </div>
        )}
      </div>

      {/* Sponsor Modal */}
      {showSponsorModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)',
          }}
        >
          <form
            onSubmit={handleSponsorSubmit}
            style={{
              background: '#ffffff',
              color: '#333333',
              padding: '32px',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '450px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
            }}
          >
            <h2 style={{ marginBottom: '24px', textAlign: 'center' }}>Add Today's Sponsor</h2>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Sponsor Name</label>
              <input
                type="text"
                value={sponsorName}
                onChange={(e) => setSponsorName(e.target.value)}
                required
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ccc' }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Image URL</label>
              <input
                type="url"
                value={sponsorImageUrl}
                onChange={(e) => setSponsorImageUrl(e.target.value)}
                required
                placeholder="https://example.com/logo.png"
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ccc' }}
              />
            </div>
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Link</label>
              <input
                type="url"
                value={sponsorLink}
                onChange={(e) => setSponsorLink(e.target.value)}
                required
                placeholder="https://sponsor-website.com"
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ccc' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                onClick={() => setShowSponsorModal(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '10px',
                  border: '1px solid #ccc',
                  background: '#f8f9fa',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '10px',
                  border: 'none',
                  background: '#27ae60',
                  color: '#ffffff',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Save Sponsor
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Next Quiz Modal */}
      {showNextQuizModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)',
          }}
        >
          <form
            onSubmit={handleNextQuizSubmit}
            style={{
              background: '#ffffff',
              color: '#333333',
              padding: '32px',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '450px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
            }}
          >
            <h2 style={{ marginBottom: '24px', textAlign: 'center' }}>Schedule Next Quiz</h2>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Quiz Title</label>
              <input
                type="text"
                value={nextQuizTitle}
                onChange={(e) => setNextQuizTitle(e.target.value)}
                placeholder="e.g. Sunday Big Cricket Quiz"
                required
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ccc' }}
              />
            </div>
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Scheduled Time</label>
              <input
                type="text"
                value={nextQuizTime}
                onChange={(e) => setNextQuizTime(e.target.value)}
                placeholder="e.g. Sunday, 7:00 PM"
                required
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ccc' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                onClick={() => setShowNextQuizModal(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '10px',
                  border: '1px solid #ccc',
                  background: '#f8f9fa',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '10px',
                  border: 'none',
                  background: '#3498db',
                  color: '#ffffff',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Announce Quiz
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Auto Play Modal */}
      {showAutoPlayModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)',
          }}
        >
          <div
            style={{
              background: '#ffffff',
              color: '#333333',
              padding: '32px',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '600px',
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
            }}
          >
            <h2 style={{ marginBottom: '24px', textAlign: 'center' }}>Auto Play Scheduler (IST)</h2>

            <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '12px', marginBottom: '24px' }}>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '15px' }}>Add New Daily Schedule</h3>
              <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem', fontWeight: 600 }}>Quiz File</label>
                  <input type="file" accept="application/json" onChange={handleScheduledFileChange} style={{ width: '100%' }} />
                  {scheduledFileName && <div style={{ fontSize: '0.8rem', color: '#27ae60', marginTop: '4px' }}>✓ {scheduledFileName}</div>}
                </div>
                <div style={{ width: '120px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem', fontWeight: 600 }}>Time (IST)</label>
                  <input
                    type="time"
                    value={autoPlayTime}
                    onChange={(e) => setAutoPlayTime(e.target.value)}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                  />
                </div>
                <button
                  onClick={handleAddSchedule}
                  style={{ padding: '10px 20px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}
                >
                  Add
                </button>
              </div>
            </div>

            <h3 style={{ fontSize: '1.2rem', marginBottom: '15px' }}>Active Schedules</h3>
            {schedules.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#888', fontStyle: 'italic' }}>No games scheduled.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {schedules.map((s) => (
                  <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 15px', background: 'rgba(52, 152, 219, 0.1)', borderRadius: '8px', border: '1px solid rgba(52, 152, 219, 0.2)' }}>
                    <div>
                      <strong style={{ fontSize: '1.1rem', color: '#2980b9' }}>{s.time} IST</strong>
                      <span style={{ marginLeft: '10px', color: '#666', fontSize: '0.9rem' }}>({s.questionCount} Questions)</span>
                    </div>
                    <button
                      onClick={() => handleDeleteSchedule(s.id)}
                      style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: '1.1rem' }}
                      title="Delete Schedule"
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div style={{ marginTop: '30px', textAlign: 'center' }}>
              <button
                onClick={() => setShowAutoPlayModal(false)}
                style={{
                  padding: '10px 30px',
                  borderRadius: '10px',
                  border: '1px solid #ccc',
                  background: '#f8f9fa',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Bar */}
      <div
        style={{
          textAlign: 'center',
          marginBottom: '20px',
          fontSize: '1.5rem',
          padding: '15px',
          background: 'rgba(255,255,255,0.15)',
          borderRadius: '12px',
          backdropFilter: 'blur(8px)',
        }}
      >
        <strong>Game Status:</strong>{' '}
        <span
          style={{
            color: !isConnected ? '#e74c3c' : gameStatus.includes('progress') ? '#2ecc71' : gameStatus.includes('Over') ? '#e74c3c' : '#f1c40f',
            fontWeight: 'bold',
          }}
        >
          {isConnected ? gameStatus : 'Connection lost... Reconnecting...'}
        </span>
        {isConnected && (
          <div style={{ marginTop: '10px', fontSize: '1.2rem', color: '#ecf0f1' }}>
            👥 <strong>Total Players Joined:</strong> <span style={{ color: '#3498db', fontSize: '1.4rem' }}>{playerCount}</span>
          </div>
        )}
      </div>

      {/* Quiz Loader & Controls – Hidden when game is in progress */}
      {!currentQuestion && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '30px',
            padding: '15px 20px',
            background: 'rgba(0,0,0,0.25)',
            borderRadius: '12px',
            gap: '16px',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: '1 1 260px' }}>
            <label style={{ fontSize: '1.3rem', marginRight: '10px' }}>
              Quiz JSON:
            </label>
            <input
              type="file"
              accept="application/json"
              onChange={handleFileChange}
              style={{ fontSize: '1.2rem' }}
            />
            <div style={{ marginTop: '8px', fontSize: '1.1rem', color: '#f1c40f' }}>
              {questionsInfo}
            </div>
          </div>
          <button
            onClick={handleStartGame}
            disabled={!isConnected || !questionsLoaded}
            style={{
              padding: '12px 24px',
              fontSize: '1.3rem',
              fontWeight: 600,
              borderRadius: '10px',
              border: 'none',
              cursor: !isConnected || !questionsLoaded ? 'not-allowed' : 'pointer',
              background: !isConnected || !questionsLoaded ? '#7f8c8d' : '#27ae60',
              color: '#ffffff',
              boxShadow: '0 4px 10px rgba(0,0,0,0.4)',
              flexShrink: 0,
            }}
          >
            Start Game
          </button>
          {questionsLoaded && (
            <button
              onClick={handleEndGame}
              style={{
                padding: '12px 24px',
                fontSize: '1.3rem',
                fontWeight: 600,
                borderRadius: '10px',
                border: 'none',
                cursor: 'pointer',
                background: '#e74c3c',
                color: '#ffffff',
                boxShadow: '0 4px 10px rgba(0,0,0,0.4)',
                flexShrink: 0,
              }}
            >
              Stop Game
            </button>
          )}
        </div>
      )}

      {/* Top Schedule Widget */}
      <div
        style={{
          background: 'rgba(255,255,255,0.06)',
          borderRadius: '24px',
          padding: '24px 30px',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.12)',
          borderTop: '6px solid #e67e22',
          boxShadow: '0 25px 60px rgba(0,0,0,0.6)',
          marginBottom: '30px',
          maxWidth: '1350px',
          marginLeft: 'auto',
          marginRight: 'auto',
          overflow: 'hidden', // Add to contain the scrolling marquee
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: '18px', color: '#e67e22', fontSize: '1.5rem', display: 'flex', alignItems: 'center', gap: '10px', textTransform: 'uppercase', letterSpacing: '1px' }}>
          📅 Today's Live Schedule
        </h3>

        <div style={{ position: 'relative', overflow: 'hidden', paddingBottom: '10px' }}>
          <style>{`
            @keyframes marquee {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            .marquee-container {
              display: flex;
              width: 200%; /* Important for infinite scroll duplicate trick */
              animation: marquee 40s linear infinite;
              gap: 12px;
            }
            .marquee-container:hover {
              animation-play-state: paused;
            }
            .schedule-card {
              flex: 0 0 calc(50% / 10 - 12px); /* 10 items divided by 50% width */
              min-width: 250px;
            }
          `}</style>
          <div className="marquee-container">
            {/* Array doubled for seamless loop */}
            {[
              ...[
                { time: "06:00 - 08:00", category: "Daily Current Affairs" },
                { time: "08:00 - 10:00", category: "English Vocab / Grammar" },
                { time: "10:00 - 13:00", category: "Static GK (History/Polity)" },
                { time: "13:00 - 14:30", category: "Bollywood & Pop Culture" },
                { time: "14:30 - 17:00", category: "Logical Reasoning / Math" },
                { time: "17:00 - 19:00", category: "Tech, Gadgets & Sci-Fi" },
                { time: "19:00 - 20:30", category: "UP/Punjab/Haryana State GK" },
                { time: "21:00 - 22:00", category: "Mega Mixed Trivia", isLive: true },
                { time: "22:00 - 00:00", category: "Cricket & Sports" },
                { time: "00:00 - 06:00", category: "Night Owl Riddles / IQ" }
              ],
              ...[
                { time: "06:00 - 08:00", category: "Daily Current Affairs" },
                { time: "08:00 - 10:00", category: "English Vocab / Grammar" },
                { time: "10:00 - 13:00", category: "Static GK (History/Polity)" },
                { time: "13:00 - 14:30", category: "Bollywood & Pop Culture" },
                { time: "14:30 - 17:00", category: "Logical Reasoning / Math" },
                { time: "17:00 - 19:00", category: "Tech, Gadgets & Sci-Fi" },
                { time: "19:00 - 20:30", category: "UP/Punjab/Haryana State GK" },
                { time: "21:00 - 22:00", category: "Mega Mixed Trivia", isLive: true },
                { time: "22:00 - 00:00", category: "Cricket & Sports" },
                { time: "00:00 - 06:00", category: "Night Owl Riddles / IQ" }
              ]
            ].map((item, idx) => {
              const now = new Date();
              const istTimeString = now.toLocaleString("en-US", { timeZone: "Asia/Kolkata" });
              const istTime = new Date(istTimeString);
              const currentHour = istTime.getHours();
              const currentMinutes = istTime.getMinutes();
              const currentTotalMinutes = currentHour * 60 + currentMinutes;

              const startHour = parseInt(item.time.substring(0, 2));
              const startMin = parseInt(item.time.substring(3, 5));
              const startTotal = startHour * 60 + startMin;

              let endHour = parseInt(item.time.substring(8, 10));
              let endMin = parseInt(item.time.substring(11, 13));
              const endTotal = endHour * 60 + endMin;

              let isActive = false;
              if (item.time === "22:00 - 00:00") {
                isActive = currentTotalMinutes >= 22 * 60;
              } else if (item.time === "00:00 - 06:00") {
                isActive = currentTotalMinutes >= 0 && currentTotalMinutes < 6 * 60;
              } else {
                isActive = currentTotalMinutes >= startTotal && currentTotalMinutes < endTotal;
              }

              return (
                <div key={idx} className="schedule-card" style={{
                  background: item.isLive ? 'rgba(231, 76, 60, 0.2)' : isActive ? 'rgba(46, 204, 113, 0.2)' : 'rgba(0,0,0,0.3)',
                  border: item.isLive ? '1px solid rgba(231, 76, 60, 0.5)' : isActive ? '1px solid rgba(46, 204, 113, 0.5)' : '1px solid rgba(255,255,255,0.05)',
                  padding: '12px 16px',
                  borderRadius: '12px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  boxShadow: (item.isLive || isActive) ? '0 4px 15px rgba(0,0,0,0.2)' : 'none',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {(isActive || item.isLive) && (
                    <div style={{
                      position: 'absolute',
                      top: 0, left: 0, width: '4px', height: '100%',
                      background: item.isLive ? '#e74c3c' : '#2ecc71'
                    }} />
                  )}
                  <div style={{ fontSize: '0.9rem', color: item.isLive ? '#ff7675' : isActive ? '#2ecc71' : '#bdc3c7', fontWeight: 700, marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    🕒 {item.time}
                    {item.isLive && <span style={{ background: '#e74c3c', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '0.7rem', flexShrink: 0 }}>LIVE STREAM</span>}
                    {isActive && !item.isLive && <span style={{ background: '#2ecc71', color: '#1a1a1a', padding: '2px 6px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 800, flexShrink: 0 }}>NOW PLAYING</span>}
                  </div>
                  <div style={{ fontSize: '1.05rem', fontWeight: 600, color: (item.isLive || isActive) ? '#fff' : '#ecf0f1', lineHeight: '1.3' }}>
                    {item.category}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content Area: Side-by-Side on Desktop */}
      <div
        style={{
          display: 'flex',
          flexDirection: isDesktop ? 'row' : 'column',
          gap: '40px',
          maxWidth: '1350px',
          margin: '0 auto',
          alignItems: 'stretch',
        }}
      >
        {/* Current Question – Left Side */}
        <div style={{ flex: 1.6, display: 'flex', flexDirection: 'column', position: 'relative' }}>
          <div
            style={{
              flex: 1,
              padding: '40px',
              background: 'rgba(255,255,255,0.06)',
              borderRadius: '24px',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderTop: '6px solid #3498db', // Iconic accent
              boxShadow: '0 25px 60px rgba(0,0,0,0.6)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              minHeight: '450px',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Question Counter Badge - Updated Creative Placement */}
            <div style={{
              position: 'absolute',
              top: '-15px',
              left: '25px',
              fontSize: currentQuestion ? '1.1rem' : '1.3rem',
              background: '#3498db',
              width: '65px',
              height: '45px',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ffffff',
              fontWeight: 900,
              zIndex: 20,
              boxShadow: '0 8px 15px rgba(52, 152, 219, 0.4)',
              border: '2px solid rgba(255,255,255,0.1)'
            }}>
              {currentQuestion ? `${currentQuestion.sequence}/${currentQuestion.total}` : '?'}
            </div>

            {currentQuestion ? (
              <>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '20px',
                    marginBottom: '45px',
                    marginTop: '20px'
                  }}
                >
                  <p
                    style={{
                      fontSize: '2.8rem',
                      fontWeight: 800,
                      lineHeight: '1.25',
                      margin: 0,
                      flex: 1,
                      textShadow: '0 4px 12px rgba(0,0,0,0.4)',
                      letterSpacing: '-0.5px'
                    }}
                  >
                    {getQuestionText()}
                  </p>
                  <div style={{ textAlign: 'right', minWidth: '180px' }}>
                    <button
                      type="button"
                      onClick={() => setLanguage((prev) => (prev === 'hindi' ? 'english' : 'hindi'))}
                      style={{
                        padding: '12px 24px',
                        borderRadius: '30px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: '#ffffff',
                        cursor: 'pointer',
                        fontSize: '1.1rem',
                        fontWeight: 700,
                        backdropFilter: 'blur(5px)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      {language === 'hindi' ? 'English' : 'हिंदी'}
                    </button>
                    <div style={{ marginTop: '12px', fontSize: '0.9rem', color: 'rgba(255,255,255,0.6)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>
                      {language === 'english' ? 'English Mode' : 'हिंदी विकल्प'}
                    </div>
                  </div>
                </div>

                {/* Question Image (optional) */}
                {currentQuestion.imageUrl && (
                  <div style={{ textAlign: 'center', margin: '20px 0' }}>
                    <img
                      src={currentQuestion.imageUrl}
                      alt="Question visual"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '300px',
                        borderRadius: '14px',
                        objectFit: 'contain',
                        boxShadow: '0 6px 24px rgba(0,0,0,0.5)',
                        border: '1px solid rgba(255,255,255,0.12)',
                      }}
                    />
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
                  {currentQuestion.options.map((opt: any) => {
                    const isCorrect = opt.id === currentQuestion.correctID;
                    const shouldReveal = autoReveal && timeLeft <= 5 && isCorrect;

                    return (
                      <div
                        key={opt.id}
                        style={{
                          padding: '28px',
                          background: shouldReveal ? 'rgba(46, 204, 113, 0.25)' : 'rgba(255,255,255,0.04)',
                          borderRadius: '18px',
                          fontSize: '1.7rem',
                          textAlign: 'center',
                          border: shouldReveal ? '3px solid #2ecc71' : '1px solid rgba(255,255,255,0.08)',
                          fontWeight: 600,
                          color: shouldReveal ? '#2ecc71' : '#ecf0f1',
                          transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                          boxShadow: shouldReveal ? '0 0 30px rgba(46, 204, 113, 0.5)' : '0 8px 25px rgba(0,0,0,0.2)',
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transform: shouldReveal ? 'scale(1.02)' : 'scale(1)'
                        }}
                      >
                        <span style={{
                          position: 'absolute',
                          left: '20px',
                          color: shouldReveal ? '#2ecc71' : 'rgba(52, 152, 219, 0.8)',
                          fontSize: '1.4rem'
                        }}>{opt.id.toUpperCase()}</span>
                        {getOptionText(opt)}
                        {shouldReveal && (
                          <div style={{
                            position: 'absolute',
                            top: '-15px',
                            right: '15px',
                            background: '#2ecc71',
                            color: '#1a1a1a',
                            fontSize: '0.9rem',
                            fontWeight: 900,
                            padding: '4px 10px',
                            borderRadius: '8px',
                            boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
                            animation: 'bounceIn 0.5s'
                          }}>
                            CORRECT ANSWER
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {totalTime > 0 && (
                  <div style={{ marginTop: '60px' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 800, color: timeLeft <= 10 ? '#e74c3c' : '#2ecc71', minWidth: '60px', textAlign: 'center' }}>
                        {timeLeft}s
                      </div>
                    </div>
                    <div
                      style={{
                        height: '12px',
                        borderRadius: '20px',
                        background: 'rgba(0,0,0,0.4)',
                        overflow: 'hidden',
                        position: 'relative'
                      }}
                    >
                      <div
                        style={{
                          height: '100%',
                          width: `${Math.max(0, Math.min(100, (timeLeft / totalTime) * 100))}%`,
                          background: timeLeft <= 10
                            ? 'linear-gradient(90deg, #e74c3c, #c0392b)'
                            : 'linear-gradient(90deg, #2ecc71, #27ae60)',
                          boxShadow: timeLeft <= 10
                            ? '0 0 20px rgba(231, 76, 60, 0.4)'
                            : '0 0 20px rgba(46, 204, 113, 0.4)',
                          transition: 'width 1s linear',
                        }}
                      />
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={{ textAlign: 'center', color: '#bdc3c7', padding: '60px 0' }}>
                <div style={{
                  fontSize: '4rem',
                  marginBottom: '30px',
                  animation: 'pulse 2s infinite ease-in-out',
                  opacity: 0.6
                }}>📡</div>
                <p style={{ fontSize: '1.8rem', fontWeight: 500, letterSpacing: '0.5px' }}>
                  Ready to stream questions...
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Leaderboard – Right Side */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: '420px', position: 'relative' }}>
          <div
            style={{
              flex: 1,
              background: 'rgba(255,255,255,0.06)',
              borderRadius: '24px',
              overflow: 'hidden',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderTop: '6px solid #f1c40f', // Iconic accent
              boxShadow: '0 25px 60px rgba(0,0,0,0.6)',
              height: '650px',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative'
            }}
          >
            {/* Trophy Badge - Updated Creative Placement */}
            <div style={{
              position: 'absolute',
              top: '-15px',
              right: '25px',
              fontSize: '1.6rem',
              background: '#f1c40f',
              width: '60px',
              height: '50px',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ffffff',
              fontWeight: 'bold',
              zIndex: 20,
              boxShadow: '0 8px 15px rgba(241, 196, 15, 0.4)',
              border: '2px solid rgba(255,255,255,0.1)'
            }}>
              🏆
            </div>

            {rankings.length === 0 ? (
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  color: '#bdc3c7',
                  padding: '40px'
                }}
              >
                <div style={{ fontSize: '4rem', marginBottom: '20px', opacity: 0.5 }}>🏁</div>
                <p style={{ fontSize: '1.7rem', textAlign: 'center', fontWeight: 500, maxWidth: '250px' }}>
                  Waiting for players to cross the line
                </p>
              </div>
            ) : (
              <div style={{ overflowY: 'auto', flex: 1, scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                <style>{`
                  ::-webkit-scrollbar { display: none; }
                `}</style>
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0' }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                    <tr style={{ background: '#1a1a1a' }}>
                      <th style={{ padding: '25px', fontSize: '1.1rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '2px', color: 'rgba(255,255,255,0.5)' }}>Rank</th>
                      <th style={{ padding: '25px', fontSize: '1.1rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '2px', color: 'rgba(255,255,255,0.5)', textAlign: 'left' }}>Elite</th>
                      <th style={{ padding: '25px', fontSize: '1.1rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '2px', color: 'rgba(255,255,255,0.5)', textAlign: 'right' }}>Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rankings.map((r, index) => (
                      <tr
                        key={r.Member}
                        style={{
                          background: index === 0 ? 'rgba(241, 196, 15, 0.08)' : 'transparent',
                          transition: 'background 0.3s ease',
                        }}
                      >
                        <td style={{
                          padding: '22px 25px',
                          fontSize: '2rem',
                          fontWeight: 900,
                          color: index === 0 ? '#f1c40f' : index === 1 ? '#bdc3c7' : index === 2 ? '#cd7f32' : 'rgba(255,255,255,0.3)',
                          textAlign: 'center',
                          borderBottom: '1px solid rgba(255,255,255,0.04)'
                        }}>
                          {index + 1}
                        </td>
                        <td style={{
                          padding: '22px 25px',
                          fontSize: '1.8rem',
                          fontWeight: 700,
                          color: index === 0 ? '#ffffff' : 'rgba(255,255,255,0.9)',
                          borderBottom: '1px solid rgba(255,255,255,0.04)'
                        }}>
                          {r.Member}
                        </td>
                        <td
                          style={{
                            padding: '22px 25px',
                            fontSize: '2rem',
                            textAlign: 'right',
                            fontWeight: 900,
                            color: index === 0 ? '#2ecc71' : '#2ecc71',
                            opacity: index === 0 ? 1 : 0.8,
                            borderBottom: '1px solid rgba(255,255,255,0.04)'
                          }}
                        >
                          {Math.floor(r.Score)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div style={{
              padding: '20px',
              textAlign: 'center',
              fontSize: '1.1rem',
              fontWeight: 700,
              color: '#f1c40f',
              background: 'rgba(0,0,0,0.2)',
              borderTop: '1px solid rgba(255,255,255,0.08)',
              textTransform: 'uppercase',
              letterSpacing: '2px'
            }}>
              Leaderboard Stats
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0% { transform: scale(1); opacity: 0.4; }
          50% { transform: scale(1.1); opacity: 0.7; }
          100% { transform: scale(1); opacity: 0.4; }
        }
      `}</style>

      {/* Footer */}
      <div
        style={{
          marginTop: '60px',
          textAlign: 'center',
          color: 'rgba(255,255,255,0.7)',
          fontSize: '1.1rem',
        }}
      >
        <p>Admin View • Real-time updates • For streaming/hosting only</p>
        <p>
          Socket: <strong style={{ color: isConnected ? '#2ecc71' : '#e74c3c' }}>
            {isConnected ? 'Connected' : 'Disconnected'}
          </strong>
        </p>
      </div>
    </div>
  );
}